<template>
	<div class="page-wrapper">
		<div class="topbar">
			<a href="https://abuse.gigahost.no/">
				<img :src="isDark ? logoDark : logoLight" alt="Gigahost" class="topbar-logo">
			</a>
			<button class="theme-toggle" @click="toggleTheme" aria-label="Toggle theme">
				<svg v-if="isDark" width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
					<circle cx="9" cy="9" r="4"/>
					<line x1="9" y1="1" x2="9" y2="3"/><line x1="9" y1="15" x2="9" y2="17"/>
					<line x1="1" y1="9" x2="3" y2="9"/><line x1="15" y1="9" x2="17" y2="9"/>
					<line x1="3.3" y1="3.3" x2="4.7" y2="4.7"/><line x1="13.3" y1="13.3" x2="14.7" y2="14.7"/>
					<line x1="3.3" y1="14.7" x2="4.7" y2="13.3"/><line x1="13.3" y1="4.7" x2="14.7" y2="3.3"/>
				</svg>
				<svg v-else width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
					<path d="M15.5 9.5a6.5 6.5 0 0 1-9-9 7 7 0 1 0 9 9z"/>
				</svg>
			</button>
		</div>

		<div class="form-container">
			<h2 class="page-title">Report Abuse</h2>

			<div class="card" v-if="step !== 3">
				<div class="card-header">
					<h3 class="card-title">Report Abuse</h3>
				</div>
				<div class="card-body">
					<p>This form is designed to facilitate the reporting of any kind of abuse associated with clients on our network. Adherence to the provided guidelines when submitting an abuse report is crucial.</p>

					<p><strong>For efficient processing and to avoid delays, it's essential to verify that the reported issue is indeed within our network, enabling us to initiate automated actions.</strong></p>

					<form @submit.prevent>
						<h4>Report Details</h4>

						<div class="form-group">
							<label for="report_type">Type of report *</label>
							<select id="report_type" class="form-control" v-model="form.type" :disabled="disableURLform">
								<option value="" selected>- Select the type of abuse</option>
								<option value="general">General</option>
								<option value="copyright">Copyright Infringement</option>
								<option value="ddos">Hacking / DoS / DDoS</option>
								<option value="fraud">Fraud / Phishing / Malware</option>
								<option value="spam">Spam</option>
								<option value="csam">Child Sexual Abuse Material (CSAM)</option>
							</select>
						</div>

						<div class="alert alert-info" v-if="form.type === 'general'">Use General to submit abuse reports that do not fit in any other category. Selecting the wrong category will automatically invalidate the report.</div>
						<div class="alert alert-info" v-if="form.type === 'copyright'">Gigahost recognizes abuse reports exclusively from copyright holders or their officially designated representatives. Each report is limited to a maximum of 250 URLs, and all URLs must be from a single domain. Additionally, we only address reports pertaining to domains that resolve to Gigahost IP addresses.</div>
						<div class="alert alert-info" v-if="form.type === 'ddos'">Gigahost strictly prohibits any form of hacking or the use of our network for Denial of Service (DoS/DDoS) attacks. We assure you that legitimate reports of hacking or DoS/DDoS activities will be promptly addressed by our team.</div>
						<div class="alert alert-info" v-if="form.type === 'fraud'">Gigahost strictly prohibits the distribution of malware or the hosting of phishing content on our service. Rest assured, legitimate reports of phishing and malware URLs will be promptly investigated and addressed by our team.</div>
						<div class="alert alert-danger" v-if="form.type === 'csam'">Gigahost is committed to responding swiftly and taking action on reports of CSAM.<br><br>Upon receiving information about domains that host images or content related to CSAM, we will take immediate action on the report.</div>

						<div class="form-group">
							<label for="report_urls">URLs *</label>
							<textarea id="report_urls" :disabled="disableURLform" placeholder="Submit one or several URLs (limit: 250), or a single IP for reporting. Ensure all URLs originate from the same domain." rows="8" class="form-control" v-model="form.urls"></textarea>
						</div>

						<div class="alert alert-danger" v-if="showAlert">The domain {{ return_domain }} is hosted on <a :href="'https://ipinfo.io/' + return_ip" target="_blank">{{ return_ip }}</a>, which is outside our network. Please contact the owner of <a :href="'https://ipinfo.io/' + return_ip" target="_blank">{{ return_ip }}</a>.</div>

						<div class="alert alert-success" v-if="showIpAlert">
							<p v-if="!showIpText">If you are sure this domain is hosted on our network then <a href="#" @click.prevent="showIpText = true">click here</a> to input the IP address.</p>
							<div v-if="showIpText">
								<p>How does it work? We will submit two requests, where one of them will use the IP you enter with the hostname set in the request header. If both pages return the same result, you can proceed with the abuse report. This lets you verify if the domain is hosted with us, but behind a proxy (for instance Cloudflare).</p>
								<small class="text-muted">You can verify by using this command: curl -v -H "Host: domain.com" 1.2.3.4/</small><br>
								<small><strong>IP-address:</strong></small>
								<input class="form-control form-control-sm" v-model="form.provided_ip" placeholder="1.2.3.4" maxlength="128">
							</div>
						</div>

						<h4>Important instructions</h4>
						<ul class="instructions-list">
							<li>A maximum of 250 URLs or 1 IP address can be reported in a single submission.</li>
							<li>All URLs in your report must share the same hostname (one report is required for each hostname).</li>
							<li>Submitting false reports or trying to circumvent our verification process may lead to a ban.</li>
							<li>Any report that is found to be invalid will not be acted upon.</li>
							<li>The time taken to respond to each report can vary, and we may not necessarily respond to all.</li>
						</ul>

						<div v-if="step === 2">
							<h4>Your Details</h4>
							<div class="form-group">
								<label for="reporter_name">Your Name *</label>
								<input class="form-control" type="text" v-model="form.reporter_name" id="reporter_name" required>
							</div>

							<div class="form-group">
								<label for="reporter_email">Your Email *</label>
								<input class="form-control" type="email" v-model="form.reporter_email" id="reporter_email" required>
							</div>

							<div class="form-group">
								<label for="reporter_organization">Organization (optional)</label>
								<input class="form-control" type="text" v-model="form.reporter_organization" id="reporter_organization">
							</div>

							<div class="form-group" v-if="['general', 'fraud', 'spam'].includes(form.type)">
								<label for="logs_evidence">Logs/Evidence (optional)</label>
								<textarea v-model="form.logs_evidence" id="logs_evidence" rows="4" placeholder="Paste relevant logs, headers, or other technical evidence here..." class="form-control"></textarea>
								<small class="text-muted">Include server logs, email headers, network traces, or other technical evidence</small>
							</div>

							<div v-if="form.type === 'copyright'" class="copyright-section">
								<h4>Copyright Information</h4>

								<div class="form-group">
									<label for="copyright_work">Description of Copyrighted Work *</label>
									<textarea v-model="form.copyright_work_description" id="copyright_work" class="form-control" rows="3" placeholder="Describe the original copyrighted work being infringed" required></textarea>
								</div>

								<div class="form-group">
									<label for="copyright_infringement">Infringement Details *</label>
									<textarea v-model="form.copyright_infringement_details" id="copyright_infringement" class="form-control" rows="3" placeholder="Explain how your work is being infringed upon" required></textarea>
								</div>

								<div class="form-group">
									<label for="copyright_authorization">Your Authority to File This Claim *</label>
									<select v-model="form.copyright_authorization" id="copyright_authorization" class="form-control" required>
										<option value="">Select your authority</option>
										<option value="owner">I am the copyright owner</option>
										<option value="agent">I am an authorized agent of the copyright owner</option>
										<option value="exclusive_licensee">I am an exclusive licensee</option>
									</select>
								</div>

								<div class="form-group checkbox-group">
									<label>
										<input type="checkbox" v-model="form.copyright_good_faith_statement" required>
										I have a good faith belief that the use is not authorized by the copyright owner, its agent, or the law *
									</label>
								</div>
							</div>

							<!-- File Uploads -->
							<div class="form-group">
								<h4>Supporting Documents</h4>

								<div v-if="form.type === 'copyright' && form.copyright_authorization === 'owner'" class="file-upload">
									<label for="owner_verification">Identity/Business Verification *</label>
									<input type="file" @change="handleFileUpload('owner_verification', $event)" id="owner_verification" class="form-control" accept=".pdf,.jpg,.jpeg,.png" required>
									<small class="text-muted">Required: Upload government-issued photo ID (individual) or business registration papers (company)</small>
								</div>

								<div v-if="form.type === 'copyright' && ['agent', 'exclusive_licensee'].includes(form.copyright_authorization)" class="file-upload">
									<label for="power_of_attorney">Power of Attorney *</label>
									<input type="file" @change="handleFileUpload('power_of_attorney', $event)" id="power_of_attorney" class="form-control" accept=".pdf,.jpg,.jpeg,.png" required>
									<small class="text-muted">Required: Upload Power of Attorney or licensing agreement document</small>
								</div>

								<div class="file-upload">
									<label for="evidence_files">Additional Evidence (optional)</label>
									<input type="file" @change="handleFileUpload('evidence', $event)" id="evidence_files" class="form-control" accept=".pdf,.jpg,.jpeg,.png,.txt,.log" multiple>
									<small class="text-muted">Screenshots, log files, or other supporting evidence</small>
								</div>
							</div>

							<!-- Uploaded Files List -->
							<div v-if="uploadedFiles.length > 0" class="uploaded-files">
								<h4>Uploaded Files</h4>
								<ul>
									<li v-for="(file, index) in uploadedFiles" :key="index">
										<span class="file-info">{{ file.name }} ({{ file.type }}) - {{ formatFileSize(file.size) }}</span>
										<button type="button" class="btn-remove" @click="removeFile(index)">Remove</button>
									</li>
								</ul>
							</div>

							<!-- Validation Messages -->
							<div v-if="validationErrors.length > 0" class="validation-errors">
								<ul>
									<li v-for="error in validationErrors" :key="error" class="error">{{ error }}</li>
								</ul>
							</div>
						</div>

						<vue-turnstile site-key="0x4AAAAAAC0sgw4HOdjmuR8h" v-model="captchaToken" :key="turnstileKey" />

						<button type="button" class="btn btn-primary" :disabled="!isVerified" @click="validateURLs" v-if="step === 1">Validate URLs</button>
						<button type="button" class="btn btn-primary" :disabled="!isVerified" @click="submitReport" v-if="step === 2">Submit report</button>
					</form>
				</div>
			</div>

			<div class="card" v-if="step === 3">
				<div class="card-body">
					<div class="alert alert-success">Thank you for submitting your report. Our team will review your report and if deemed valid forward the report to the responsible client. Please expect a processing time between 24 hours and 10 business days, depending on the severity of the report.</div>
				</div>
			</div>
		</div>

		<div class="footbar">
			&copy; {{ currentYear }} - Gigahost AS
		</div>
	</div>
</template>

<script>
import VueTurnstile from 'vue-turnstile'
import logoLight from '../assets/logo.png'
import logoDark from '../assets/logo-dark.png'

export default {
	name: 'ReportAbuse',
	components: {
		VueTurnstile
	},
	data() {
		return {
			logoLight,
			logoDark,
			isDark: document.documentElement.getAttribute('data-theme') === 'dark',
			form: {
				type: '',
				reporter_name: '',
				reporter_email: '',
				reporter_organization: '',
				urls: '',
				provided_ip: '',
				description: '',
				logs_evidence: '',
				copyright_work_description: '',
				copyright_infringement_details: '',
				copyright_authorization: '',
				copyright_good_faith_statement: false
			},
			disableURLform: false,
			isVerified: false,
			step: 1,
			showIpAlert: false,
			showIpText: false,
			showAlert: false,
			return_domain: '',
			return_ip: '',
			uploadedFiles: [],
			isSubmitting: false,
			captchaToken: '',
			turnstileKey: 0,
			validationErrors: []
		}
	},
	computed: {
		currentYear() {
			return new Date().getFullYear()
		},
		canSubmit() {
			return this.inputValidation.valid &&
				this.form.type &&
				this.form.reporter_name &&
				this.form.reporter_email &&
				this.form.description &&
				this.validateCopyrightRequirements
		},
		validateCopyrightRequirements() {
			if (this.form.type !== 'copyright') return true
			return this.form.copyright_work_description &&
				this.form.copyright_infringement_details &&
				this.form.copyright_authorization &&
				this.form.copyright_good_faith_statement &&
				this.hasRequiredFiles()
		},
		inputValidation() {
			if (!this.form.urls || this.form.urls.trim() === '') {
				return { valid: false, error: 'No URLs or IP addresses submitted' }
			}

			const lines = this.form.urls.trim().split('\n').filter(line => line.trim() !== '')

			if (lines.length === 0) {
				return { valid: false, error: 'No URLs or IP addresses submitted' }
			}

			const ipv4Pattern = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
			const ipv6Pattern = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::1$|^::$/
			const urlPattern = /^https?:\/\/[^\s/$.?#].[^\s]*$/i

			let ipCount = 0
			let urlCount = 0
			let domains = new Set()

			for (const line of lines) {
				const trimmedLine = line.trim()

				if (ipv4Pattern.test(trimmedLine) || ipv6Pattern.test(trimmedLine)) {
					ipCount++
				} else if (urlPattern.test(trimmedLine)) {
					urlCount++
					try {
						const url = new URL(trimmedLine)
						domains.add(url.hostname)
					} catch (e) {
						return { valid: false, error: `Invalid URL format: ${trimmedLine}` }
					}
				} else if (trimmedLine !== '') {
					return { valid: false, error: `Invalid format: ${trimmedLine}` }
				}
			}

			if (ipCount === 0 && urlCount === 0) {
				return { valid: false, error: 'No URLs or IP addresses submitted' }
			}
			if (ipCount > 0 && urlCount > 0) {
				return { valid: false, error: 'Cannot mix IP addresses and URLs' }
			}
			if (ipCount > 1) {
				return { valid: false, error: 'Only one IP address allowed' }
			}
			if (urlCount > 250) {
				return { valid: false, error: 'Too many URLs submitted (maximum 250 allowed)' }
			}
			if (urlCount > 0 && domains.size > 1) {
				return { valid: false, error: 'All URLs must be from the same domain' }
			}

			return { valid: true, error: null }
		}
	},
	watch: {
		captchaToken(val) {
			this.isVerified = !!val
		}
	},
	methods: {
		toggleTheme() {
			this.isDark = !this.isDark
			if (this.isDark) {
				document.documentElement.setAttribute('data-theme', 'dark')
				localStorage.setItem('theme', 'dark')
			} else {
				document.documentElement.removeAttribute('data-theme')
				localStorage.setItem('theme', 'light')
			}
		},
		resetForm() {
			this.form = {
				type: '',
				reporter_name: '',
				reporter_email: '',
				reporter_organization: '',
				urls: '',
				provided_ip: '',
				description: '',
				logs_evidence: '',
				copyright_work_description: '',
				copyright_infringement_details: '',
				copyright_authorization: '',
				copyright_good_faith_statement: false
			}
			this.uploadedFiles = []
			this.validationErrors = []
		},
		handleFileUpload(type, event) {
			const files = Array.from(event.target.files)
			files.forEach(file => {
				this.uploadedFiles.push({
					file: file,
					name: file.name,
					type: type,
					size: file.size
				})
			})
		},
		removeFile(index) {
			this.uploadedFiles.splice(index, 1)
		},
		hasRequiredFiles() {
			if (this.form.type !== 'copyright') return true
			if (this.form.copyright_authorization === 'owner') {
				return this.uploadedFiles.some(f => f.type === 'owner_verification')
			} else if (['agent', 'exclusive_licensee'].includes(this.form.copyright_authorization)) {
				return this.uploadedFiles.some(f => f.type === 'power_of_attorney')
			}
			return false
		},
		formatFileSize(bytes) {
			if (bytes === 0) return '0 Bytes'
			const k = 1024
			const sizes = ['Bytes', 'KB', 'MB', 'GB']
			const i = Math.floor(Math.log(bytes) / Math.log(k))
			return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
		},
		validateURLs() {
			const validation = this.inputValidation
			if (validation.valid) {
				const formData = new FormData()
				formData.append('type', this.form.type)
				formData.append('urls', this.form.urls)
				if (this.form.provided_ip) {
					formData.append('ip', this.form.provided_ip)
				}
				formData.append('token', this.captchaToken)
				formData.append('cf-turnstile-response', this.captchaToken)

				this.$http.post('abuse/validate', formData, {
					headers: { 'Content-Type': 'multipart/form-data' }
				}).then(() => {
					this.step = 2
					this.disableURLform = true
					this.turnstileKey++
					this.notify('success', 'URLs/IP validated successfully, please complete the report')
				}).catch(response => {
					this.turnstileKey++
					const msg = response.response?.data?.meta?.message || 'Validation failed'
					this.notify('error', msg)

					const responseData = response.response?.data?.meta
					if (responseData?.message && responseData.message.includes('does not resolve to an IP')) {
						this.showIpAlert = true
						this.return_domain = responseData.domain || ''
						this.return_ip = responseData.resolved_ips ? responseData.resolved_ips[0] : ''
						this.showAlert = true
					}
				})
			} else {
				this.notify('error', validation.error)
			}
		},
		submitReport() {
			const validation = this.inputValidation
			if (validation.valid) {
				const formData = new FormData()
				formData.append('type', this.form.type)
				formData.append('urls', this.form.urls)
				if (this.form.provided_ip) {
					formData.append('ip', this.form.provided_ip)
				}
				formData.append('token', this.captchaToken)
				formData.append('cf-turnstile-response', this.captchaToken)
				formData.append('reporter_name', this.form.reporter_name || '')
				formData.append('reporter_email', this.form.reporter_email || '')
				formData.append('reporter_organization', this.form.reporter_organization || '')
				formData.append('description', this.form.description || '')
				formData.append('logs_evidence', this.form.logs_evidence || '')

				if (this.form.type === 'copyright') {
					formData.append('copyright_work_description', this.form.copyright_work_description || '')
					formData.append('copyright_infringement_details', this.form.copyright_infringement_details || '')
					formData.append('copyright_authorization', this.form.copyright_authorization || '')
					formData.append('copyright_good_faith_statement', this.form.copyright_good_faith_statement ? '1' : '0')
				}

				if (this.uploadedFiles && this.uploadedFiles.length > 0) {
					this.uploadedFiles.forEach((fileObj, index) => {
						formData.append(`files[${index}][file]`, fileObj.file)
						formData.append(`files[${index}][type]`, fileObj.type)
					})
				}

				this.$http.post('abuse', formData, {
					headers: { 'Content-Type': 'multipart/form-data' }
				}).then(() => {
					this.step = 3
				}).catch(response => {
					this.turnstileKey++
					const msg = response.response?.data?.meta?.message || 'Submission failed'
					this.notify('error', msg)

					const responseData = response.response?.data?.meta
					if (responseData?.message && responseData.message.includes('does not resolve to IPs')) {
						this.showIpAlert = true
						this.return_domain = responseData.domain || ''
						this.return_ip = responseData.resolved_ips ? responseData.resolved_ips[0] : ''
						this.showAlert = true
					}
				})
			} else {
				this.notify('error', validation.error)
			}
		}
	}
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

.page-wrapper {
	min-height: 100vh;
	background: var(--color-bg-page);
	font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
	color: var(--color-text-primary);
}

.topbar {
	max-width: 900px;
	margin: 0 auto;
	padding: 24px 16px 0;
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.topbar-logo {
	width: 180px;
	height: auto;
}

.theme-toggle {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	border: 1px solid var(--color-border-card);
	border-radius: 8px;
	background: var(--color-bg-card);
	color: var(--color-text-muted);
	cursor: pointer;
	transition: border-color 0.15s, color 0.15s;
}

.theme-toggle:hover {
	border-color: var(--color-border-card-hover);
	color: var(--color-text-primary);
}

.form-container {
	max-width: 900px;
	margin: 0 auto;
	padding: 32px 16px 80px;
}

.page-title {
	font-size: 22px;
	font-weight: 600;
	margin-bottom: 20px;
}

.card {
	background: var(--color-bg-card);
	border: 1px solid var(--color-border-card);
	border-radius: 14px;
	overflow: hidden;
}

.card-header {
	padding: 18px 24px;
	border-bottom: 1px solid var(--color-bg-divider);
}

.card-title {
	font-size: 16px;
	font-weight: 600;
	margin: 0;
}

.card-body {
	padding: 24px;
	font-size: 14px;
	line-height: 1.6;
}

.card-body p {
	margin-bottom: 14px;
	color: var(--color-text-secondary);
}

.card-body strong {
	color: var(--color-text-primary);
}

h4 {
	font-size: 14px;
	font-weight: 600;
	margin: 24px 0 12px;
	color: var(--color-text-primary);
}

.form-group {
	margin-bottom: 16px;
}

.form-group label {
	display: block;
	font-size: 13px;
	font-weight: 500;
	color: var(--color-text-secondary);
	margin-bottom: 6px;
}

.form-control {
	width: 100%;
	border: 1.5px solid var(--color-border-input);
	border-radius: 10px;
	padding: 12px 16px;
	font-size: 14px;
	font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
	background: var(--color-bg-card);
	color: var(--color-text-primary);
	transition: border-color 0.15s;
	box-sizing: border-box;
}

.form-control:focus {
	outline: none;
	border-color: var(--color-border-input-focus);
}

.form-control::placeholder {
	color: var(--color-text-faint);
}

.form-control:disabled {
	background: var(--color-bg-subtle);
	color: var(--color-text-muted);
	cursor: not-allowed;
}

.form-control-sm {
	padding: 8px 12px;
	font-size: 13px;
	border-radius: 8px;
}

select.form-control {
	appearance: none;
	background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%235c5e6b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
	background-repeat: no-repeat;
	background-position: right 14px center;
	padding-right: 40px;
}

textarea.form-control {
	resize: vertical;
	min-height: 80px;
}

.alert {
	padding: 14px 18px;
	border-radius: 10px;
	font-size: 13.5px;
	line-height: 1.5;
	margin-bottom: 16px;
	border: 1px solid;
}

.alert-info {
	background: var(--color-alert-info-bg);
	border-color: var(--color-alert-info-border);
	color: var(--color-alert-info-text);
}

.alert-danger {
	background: var(--color-alert-danger-bg);
	border-color: var(--color-alert-danger-border);
	color: var(--color-alert-danger-text);
}

.alert-success {
	background: var(--color-alert-success-bg);
	border-color: var(--color-alert-success-border);
	color: var(--color-alert-success-text);
}

.alert a {
	color: inherit;
	font-weight: 600;
	text-decoration: underline;
}

.instructions-list {
	list-style-type: disc;
	padding-left: 20px;
	margin-bottom: 16px;
}

.instructions-list li {
	display: list-item;
	margin: 0 0 6px 0;
	font-size: 13.5px;
	color: var(--color-text-secondary);
	line-height: 1.5;
}

.btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: none;
	cursor: pointer;
	font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
	font-size: 14px;
	font-weight: 600;
	transition: background-color 0.15s;
}

.btn-primary {
	background: var(--color-btn-primary-bg);
	color: var(--color-btn-primary-text);
	border-radius: 10px;
	padding: 12px 24px;
	margin-top: 8px;
}

.btn-primary:hover {
	background: var(--color-btn-primary-hover);
}

.btn-primary:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.text-muted {
	color: var(--color-text-muted);
	font-size: 12px;
}

.checkbox-group label {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	cursor: pointer;
}

.checkbox-group input[type="checkbox"] {
	margin-top: 3px;
	flex-shrink: 0;
}

.file-upload {
	margin-bottom: 16px;
}

.uploaded-files ul {
	list-style: none;
	padding: 0;
}

.uploaded-files li {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 0;
	border-bottom: 1px solid var(--color-bg-divider);
	font-size: 13px;
	margin: 0;
}

.file-info {
	color: var(--color-text-secondary);
}

.btn-remove {
	background: none;
	border: 1px solid var(--color-btn-danger-border);
	color: var(--color-btn-danger-text);
	border-radius: 6px;
	padding: 4px 10px;
	font-size: 12px;
	cursor: pointer;
	font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}

.btn-remove:hover {
	background: var(--color-btn-danger-hover-bg);
}

.validation-errors ul {
	list-style: none;
	padding: 0;
}

.validation-errors .error {
	color: var(--color-status-danger-text);
	font-size: 13px;
	margin: 0 0 4px 0;
	display: block;
}

.copyright-section {
	padding: 16px 0;
}

.footbar {
	max-width: 900px;
	margin: 0 auto;
	padding: 40px 16px;
	text-align: center;
	font-size: 12px;
	color: var(--color-text-muted);
}

@media (max-width: 600px) {
	.form-container {
		padding: 20px 12px 60px;
	}

	.card-body {
		padding: 16px;
	}
}
</style>
